import React, { Fragment, useState } from 'react'
import { ItemDetails } from 'components'
import { Box, styled, Typography, useTheme } from '@mui/material'
import { DifferentiationValueIcon } from 'packages/eid-icons'
import { EidTree } from 'packages/eid-controls'
import { useIsSmallScreen } from 'packages/core'
import { useCheckAzureRbacRoleAccess, useTargetPerson } from 'hooks'
import { useTranslation } from 'react-i18next'
import cartHelpers from 'containers/Cart/cartHelpers'
import { GlobalSensitiveFunctions } from './GlobalSensitiveFunctions'
import { OverviewSection } from './OverviewSection'
import { ResourceTypesNamespace, useRegistry } from 'core'
import { AddToCart } from 'components/AddToCart'
import { Skeleton } from '@mui/material'
import ActivateNowButton from 'containers/ListingPage/ActivateNowButton'
import useConfiguration from 'useConfiguration'
import AdditionalInformationSection from './MoreInformation'
import TextWithIcon from 'components/common/TextWithIcon'
import { ROLE_TYPE_ID } from 'utils'

const azureRbacRolePrimaryTabExplain = {
    overview:
        'Overview tab: Azure RBAC role system type, role type, tenant, description, common policy attributes, and any published instructions.',
    additionalInformation:
        'More information tab: owners, approvers, responsible party, and local sensitive functions when enabled.',
}

const PaddedDiv = styled('div')({
    padding: '0 31px',
})

const PromptContainer = styled('div')({
    padding: '48px 31px',
    minHeight: '232px',
    display: 'flex',
})

const AzureRbacRoleDetails = ({ azureRbacRole, toggleDrawer }) => {
    const { t } = useTranslation()
    const theme = useTheme()

    const registry = useRegistry()

    const azureRbacRoleType = registry.get(
        ResourceTypesNamespace,
        'AzureRbacRoles',
    )
    const { getResourceType } = useConfiguration()

    const currentResourceType = getResourceType('AzureRoles')
    const isSmallScreen = useIsSmallScreen()

    const [activeTab, setActiveTab] = useState('overview')

    const [targetPerson] = useTargetPerson()
    const [location, setLocation] = useState(null)

    const { data: assignmentStatus, isInitialLoading: isCheckingAccess } =
        useCheckAzureRbacRoleAccess(azureRbacRole.id, location?.id)

    const preAdd = (baseObj) => {
        const itemToAdd = cartHelpers.azRbacRoleToCartItem({
            targetPerson,
            assignmentType: 'Add',
            azureRbacRole,
            assignmentStatus,
            location,
            ...baseObj,
        })
        if (!itemToAdd) return undefined

        return itemToAdd
    }

    const getPreValidationMessage = () => {
        if (!Boolean(location)) {
            return t('AzureRbacRoles_SelectScope')
        }
        if (assignmentStatus && assignmentStatus.isAssigned) {
            return t('ResourceAlreadyAssigned')
        }
        return ''
    }
    return (
        <Fragment>
            <ItemDetails.Tabs
                variant="standard"
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
            >
                <ItemDetails.Tabs.Tab
                    value="overview"
                    label={t('Common_Overview')}
                    data-ai-explain={azureRbacRolePrimaryTabExplain.overview}
                />
                <ItemDetails.Tabs.Tab
                    value="additionalInformation"
                    label={t('Common_MoreInformation')}
                    data-ai-explain={
                        azureRbacRolePrimaryTabExplain.additionalInformation
                    }
                />
            </ItemDetails.Tabs>

            {activeTab === 'overview' && (
                <OverviewSection azureRbacRole={azureRbacRole} />
            )}
            {activeTab === 'additionalInformation' && (
                <AdditionalInformationSection rbacRole={azureRbacRole} />
            )}

            {!isSmallScreen && (
                <PromptContainer>
                    <Box
                        width="172px"
                        display="flex"
                        flexDirection="column"
                        zIndex={1}
                        data-ai-explain="Azure RBAC scope selection (desktop): this column summarizes the task; pick a subscription, resource group, or resource in the tree to the right (not the root node)."
                    >
                        <Typography
                            style={{
                                fontSize: '14px',
                                lineHeight: 1.43,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                            }}
                        >
                            {t('AzureRbacRoles_SelectScope')}
                        </Typography>
                        <Box
                            marginTop="24px"
                            width="100%"
                            display="flex"
                            justifyContent="center"
                        >
                            <DifferentiationValueIcon />
                        </Box>
                    </Box>

                    <Box style={{ flexGrow: 1 }} paddingLeft="16px">
                        <Box
                            data-ai-explain="Scope tree: select a node with a parent (not the root) so the role can be assigned at that Azure location; optional scope-specific instructions appear below."
                        >
                            <EidTree
                                url={`/api/AzureRoles/rbacRoleLocations/${azureRbacRole.id}`}
                                onChange={(item) => {
                                    setLocation(item)
                                }}
                                value={location}
                                searchable
                                searchPlaceholder={t('Common_Search')}
                                searchContainerStyle={{
                                    backgroundColor:
                                        theme?.palette?.background?.paper,
                                    width: '352px',
                                }}
                                treeContainerStyle={{
                                    paddingX: '10px',
                                    width: '100%',
                                    padding: '10px',
                                    overflow: 'auto',
                                    borderRadius: '5px',
                                    boxShadow:
                                        '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
                                    backgroundColor:
                                        theme?.palette?.common?.white,
                                }}
                                radioSelection={false}
                                otherPropertiesToGet={['instructions']}
                            />
                        </Box>
                        {location?.instructions && (
                            <Box
                                sx={{ mt: '3.2rem' }}
                                data-ai-explain="Scope-specific instructions from the catalog for the Azure node you selected in the tree."
                            >
                                <TextWithIcon
                                    iconName={'InfoNew'}
                                    message={location?.instructions}
                                />
                            </Box>
                        )}
                    </Box>
                </PromptContainer>
            )}

            {isSmallScreen && (
                <PaddedDiv>
                    <Box
                        paddingY="24px"
                        display="flex"
                        flexDirection="column"
                        position="relative"
                        data-ai-explain="Azure RBAC scope (mobile): choose a non-root scope in the searchable tree before requesting access or activating."
                    >
                        <Box position="absolute" top="16px" right="0">
                            <DifferentiationValueIcon
                                width="107px"
                                height="100px"
                            />
                        </Box>
                        <Box width="172px" paddingY="4px" zIndex={1}>
                            <Typography
                                style={{
                                    fontSize: '14px',
                                    lineHeight: 1.43,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                }}
                            >
                                {t('AzureRbacRoles_SelectScope')}
                            </Typography>
                        </Box>
                        <Box marginTop="32px" paddingY="4px" zIndex={1}>
                            <Box
                                width="100%"
                                paddingY="10px"
                                height="232px"
                                overflow="auto"
                                borderRadius="5px"
                                boxShadow="0 2px 16px 0 rgba(0, 0, 0, 0.11)"
                                style={{
                                    backgroundColor:
                                        theme?.palette?.background?.paper,
                                }}
                                data-ai-explain="Scrollable scope tree for this Azure RBAC role; pick a child node under your tenant or subscription."
                            >
                                <EidTree
                                    url={`/api/AzureRoles/rbacRoleLocations/${azureRbacRole.id}`}
                                    onChange={(item) => setLocation(item)}
                                    value={location}
                                    searchable
                                    searchPlaceholder={t('Common_Search')}
                                />
                            </Box>
                        </Box>
                    </Box>
                </PaddedDiv>
            )}
            <Box
                data-ai-explain="Access actions: after a valid Azure scope is selected (not the tree root), check eligibility, then activate immediately if allowed or add this RBAC role to the cart to request access."
            >
                {location && location?.parentId !== null && (
                    <>
                        {isCheckingAccess ? (
                            <Box padding={'24px'}>
                                <Skeleton height={40} />
                            </Box>
                        ) : assignmentStatus?.canActivateNow ? (
                            <ActivateNowButton
                                resource={azureRbacRole}
                                resourceType={currentResourceType}
                                forDetailsDrawer={true}
                                width="300px"
                                locationId={
                                    azureRbacRole?.roleTypeId ===
                                    ROLE_TYPE_ID.SCOPED_ROLE
                                        ? location?.id
                                        : ''
                                }
                            />
                        ) : (
                            <Box
                                data-ai-explain="Add this Azure RBAC role at the selected scope to the cart to start an access request (subject to policy and validation)."
                            >
                                <AddToCart
                                    resourceType={azureRbacRoleType}
                                    resource={azureRbacRole}
                                    secondary={location}
                                    preValidate={getPreValidationMessage}
                                    preAdd={preAdd}
                                    postAdd={toggleDrawer}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {location && (
                <GlobalSensitiveFunctions
                    azureRbacRole={azureRbacRole}
                    locationId={location?.id}
                />
            )}
        </Fragment>
    )
}

export default AzureRbacRoleDetails
